import { useCallback } from 'react';
import posthog from 'posthog-js';
import type { Properties, EventName } from '@posthog/types';
import { ReportBuilderData } from '../providers/ReportProvider/utils';
import { UserType } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { POST_HOG_HOST, POST_HOG_TOKEN } from './constants';

interface LogRocketInstance {
  init(appId: string): void;
  identify(uid: string | number, traits?: Record<string, unknown>): void;
  track(eventName: string, properties?: Record<string, unknown>): void;
}

declare global {
  interface Window {
    LogRocket?: LogRocketInstance;
  }
}

interface TrackEventOptions {
  identify?: boolean;
}

const logRocket = window.LogRocket;
export const useTrackEvent = () => {
  const trackEvent = useCallback(
    (
      eventName: EventName,
      payload: Properties,
      options?: TrackEventOptions,
    ) => {
      const userId = payload['custom:user_id'];
      if (!POST_HOG_HOST || !POST_HOG_TOKEN) {
        return;
      }
      if (options?.identify && userId) {
        posthog?.identify(
          userId.toString(),
          payload,
        );
        logRocket?.identify(userId, payload);
        return;
      }
      posthog.capture(eventName, payload);
      logRocket?.track(eventName, payload);
    },
    [],
  );

  return trackEvent;
};

export const getPostHogSessionId = (): string | null => {
  try {
    return posthog?.get_session_id?.() ?? null;
  } catch {
    return null;
  }
};

export const pullReportData = (
  reportData: ReportBuilderData,
  isReportPassed: boolean,
  userType: UserType | undefined
) => {
  const firstAllocation = reportData.allocations[0];
  const firstPortfolio = firstAllocation?.investment?.portfolio;
  return {
    userType,
    reportId: reportData.dbId,
    year: reportData.year,
    stage: reportData.stage,
    status: reportData.status,
    visibility: reportData.visibility,
    programName: reportData.programName,
    clonedFromId: reportData.clonedFromId,
    startDate: reportData.startDate,
    endDate: reportData.endDate,
    feedbackType: reportData.feedbackType,
    cnOptedIn: reportData.cnOptedIn,
    totalDonations: reportData.totalDonations,
    whereReportCreated: reportData.cnOptedIn ? 'CN' : 'TI',
    fromScratch: !reportData.clonedFromId,
    firstPortfolio: firstPortfolio?.name,
    firstDonorName: firstPortfolio?.funder?.name,
    firstDonorEmail: firstPortfolio?.relationshipManager?.email,
    isReportPassed,
  };
};
